{
  "leave": {
    "title": "Discard unsaved changes?",
    "body": "You have changes on this page that haven't been saved.",
    "confirm": "Discard changes",
    "cancel": "Keep editing"
  },
  "errors": {
    "WAREHOUSE_ARCHIVED": "An archived warehouse can't be used. Choose an active one.",
    "WAREHOUSE_MAIN": "This is the main warehouse. Make another warehouse the main one first.",
    "WAREHOUSE_NOT_EMPTY": "This warehouse still holds stock. Move or adjust the remaining stock first.",
    "WAREHOUSE_IN_USE": "Tills sell from this warehouse ({{tills}}). Give them another warehouse under Point of sale › Tills first.",
    "NO_MAIN_WAREHOUSE": "There's no main warehouse. Set one up under Warehouses first.",
    "COST_REQUIRED": "Stock added needs a cost per unit. Enter it on each line that adds stock.",
    "INVALID_COST": "A cost can't be negative. Check the costs on the lines.",
    "WHOLE_UNITS_ONLY": "A product on this adjustment is counted in whole units. Remove the decimals.",
    "INVALID_MOVEMENT": "A line doesn't change the stock. Remove it or enter a quantity.",
    "STOCK_ALREADY_USED": "Some of the stock this adjustment added has already been sold or used. Record a new adjustment instead of voiding.",
    "SERIAL_COUNT_MISMATCH": "A serial-tracked line needs one serial number per unit. Check the serial numbers on the lines.",
    "SERIAL_NOT_AVAILABLE": "Some serial numbers aren't available here: {{serials}}. Choose them again.",
    "SERIAL_DUPLICATE": "Some serial numbers are listed twice, or are already in stock or sold: {{serials}}.",
    "SERIALS_NOT_TRACKED": "A product on this document doesn't track serial numbers. Remove its serial numbers.",
    "SERIAL_MOVED_ON": "Some of its serial numbers have moved on since (sold, transferred or removed), so it can't be voided: {{serials}}."
  },
  "reason": {
    "OPENING": "Opening stock",
    "PURCHASE": "Purchase",
    "PURCHASE_RETURN": "Return to supplier",
    "SALE": "Sale",
    "SALE_RETURN": "Customer return",
    "TRANSFER_OUT": "Transfer out",
    "TRANSFER_IN": "Transfer in",
    "ADJUSTMENT": "Adjustment",
    "PROJECT_ISSUE": "Issued to a project",
    "PROJECT_RETURN": "Returned from a project"
  },
  "source": {
    "StockAdjustment": "Stock adjustment",
    "SalesInvoice": "Sales invoice",
    "CreditNote": "Credit note",
    "Purchase": "Purchase",
    "SupplierReturn": "Supplier return",
    "ProjectMaterialIssue": "Material issue",
    "StockTransfer": "Stock transfer"
  },
  "warehouses": {
    "title": "Warehouses",
    "subtitle": "Where stock is kept. The main warehouse is used when a document doesn't name one.",
    "new": "New warehouse",
    "newTitle": "New warehouse",
    "newSubtitle": "The first warehouse becomes the main one.",
    "editTitle": "Edit warehouse",
    "save": "Save warehouse",
    "actions": "Actions",
    "showArchived": "Show archived",
    "empty": "No warehouses yet.",
    "emptyBody": "Add the store or shop where you keep your stock.",
    "main": "Main",
    "archived": "Archived",
    "stock": "Stock",
    "stockOf": "Stock in {{name}}",
    "editNamed": "Edit {{name}}",
    "moreNamed": "More actions for {{name}}",
    "restoreNamed": "Restore {{name}}",
    "setMain": "Make main warehouse",
    "fields": {
      "code": "Code",
      "name": "Name",
      "nameAr": "Name in Arabic",
      "status": "Status"
    },
    "hints": {
      "code": "A short code shown on stock movements, such as MAIN or SHOP-2."
    },
    "mainDialog": {
      "title": "Make {{name}} the main warehouse?",
      "body": "Documents that don't name a warehouse will take stock from it."
    },
    "archive": {
      "title": "Archive {{name}}?",
      "body": "It can no longer receive or issue stock. Only an empty warehouse that isn't the main one can be archived.",
      "confirm": "Archive"
    },
    "restore": {
      "title": "Restore {{name}}?",
      "body": "It can receive and issue stock again.",
      "confirm": "Restore"
    },
    "errors": {
      "codeTaken": "Another warehouse already has this code.",
      "version": "Someone else changed this warehouse. Close this panel and open it again."
    },
    "toast": {
      "created": "Warehouse added",
      "updated": "Warehouse saved",
      "archived": "Warehouse archived",
      "restored": "Warehouse restored",
      "main": "{{name}} is now the main warehouse"
    },
    "movements": "Stock movements",
    "figures": {
      "value": "Value",
      "items": "Items held",
      "low": "To reorder"
    }
  },
  "stock": {
    "title": "Stock levels",
    "subtitle": "What is on hand, at weighted-average cost, in one warehouse or all of them.",
    "adjust": "Adjust stock",
    "searchPlaceholder": "Search name, SKU or barcode…",
    "searchLabel": "Search stock",
    "clearSearch": "Clear search",
    "warehouseFilter": "Warehouse",
    "allWarehouses": "All warehouses",
    "belowReorder": "At or below reorder level",
    "low": "At or below the reorder level",
    "rowHint": "Open a row to see its stock movements.",
    "pageValue": "Value on this page",
    "clearFilters": "Clear filters",
    "fields": {
      "product": "Product",
      "sku": "SKU",
      "onHand": "On hand",
      "reorderLevel": "Reorder level",
      "averageCost": "Average cost",
      "value": "Value"
    },
    "empty": {
      "title": "No stocked products yet.",
      "body": "Goods appear here once they are added as products.",
      "filteredTitle": "Nothing matches.",
      "filteredBody": "Try another search, or show every product."
    },
    "rowActions": "Actions for {{name}}",
    "actions": {
      "movements": "Movements",
      "adjust": "Adjust stock",
      "count": "Count stock",
      "transfer": "Transfer",
      "product": "Open product"
    },
    "stats": {
      "label": "Stock figures",
      "items": "Items",
      "itemsDetail": "Products and variants on the list",
      "low": "At or below reorder",
      "lowDetail": "Time to reorder",
      "lowShare": "{{percent}}% of the items are at or below their reorder level",
      "out": "Out of stock",
      "outDetail": "Products with nothing on hand",
      "value": "Value at cost",
      "valueDetail_one": "{{n}} item at weighted-average cost",
      "valueDetail_other": "{{n}} items at weighted-average cost",
      "unavailable": "Figures unavailable"
    }
  },
  "movements": {
    "title": "Stock movements",
    "subtitle": "Every receipt and issue of stock, newest first.",
    "productFilter": "Product",
    "allProducts": "All products",
    "selectedProduct": "Selected product",
    "clearProduct": "Show all products",
    "warehouseFilter": "Warehouse",
    "allWarehouses": "All warehouses",
    "balanceHint": "Choose a product and a warehouse to see the balance after each movement.",
    "clearFilters": "Clear filters",
    "fields": {
      "date": "Date",
      "warehouse": "Warehouse",
      "product": "Product",
      "reason": "Reason",
      "quantity": "Quantity",
      "unitCost": "Unit cost",
      "value": "Value",
      "balanceAfter": "Balance after",
      "source": "Source",
      "createdBy": "By"
    },
    "empty": {
      "title": "No stock movements yet.",
      "body": "Opening stock, purchases, sales and adjustments appear here.",
      "filteredTitle": "No movements match.",
      "filteredBody": "Try another product, warehouse or period, or clear the filters."
    },
    "system": "System",
    "reasonFilter": "Movement type",
    "allReasons": "All types",
    "productChip": "Product: {{name}}"
  },
  "adjustments": {
    "newTitle": "New stock adjustment",
    "newSubtitle": "Posted to the stock and the ledger straight away. The number is assigned when you post.",
    "prefillFailed": "The product could not be loaded. Pick it on the line instead.",
    "post": "Post adjustment",
    "saveHint": "Press {{shortcut}} to post",
    "movements": "Stock movements",
    "status": {
      "POSTED": "Posted",
      "VOID": "Void"
    },
    "kind": {
      "OPENING": "Opening stock",
      "ADJUSTMENT": "Adjustment",
      "COUNT": "Stock count"
    },
    "kindHint": {
      "OPENING": "Stock you already had when you started, received at its cost.",
      "ADJUSTMENT": "Add or remove quantities: damage, loss or stock found. Increases need a cost.",
      "COUNT": "Enter what you counted; the difference to what's on hand is applied."
    },
    "linesHint": {
      "OPENING": "Enter the quantity on hand and the cost per unit of each product.",
      "ADJUSTMENT": "Enter the change: 5 adds five, -2 removes two. Removed stock goes out at its average cost.",
      "COUNT": "Enter the quantity counted. A surplus is valued at the cost you enter, or at the average cost."
    },
    "quantityHeader": {
      "OPENING": "Quantity",
      "ADJUSTMENT": "Change",
      "COUNT": "Counted"
    },
    "sections": {
      "adjustment": "Adjustment",
      "lines": "Products"
    },
    "fields": {
      "date": "Date",
      "warehouse": "Warehouse",
      "kind": "Kind",
      "notes": "Notes",
      "product": "Product",
      "unitCost": "Unit cost",
      "value": "Value",
      "delta": "Change",
      "totalValue": "Total value",
      "createdAt": "Recorded",
      "number": "Number",
      "status": "Status",
      "reason": "Reason",
      "createdBy": "Recorded by",
      "systemQuantity": "On hand",
      "difference": "Difference",
      "journalEntry": "Journal entry"
    },
    "chooseWarehouse": "Choose a warehouse",
    "chooseProduct": "Choose a product",
    "addLine": "Add product",
    "removeLine": "Remove line {{n}}",
    "lineActions": "Line actions",
    "enterHint": "Enter in the last line adds another.",
    "totalKnown": "Value of stock added",
    "productOfLine": "Product on line {{n}}",
    "quantityOfLine": "Quantity on line {{n}}",
    "costOfLine": "Unit cost on line {{n}}",
    "atAverage": "At average cost",
    "optional": "Optional",
    "valuedOnPosting": "Set when posted",
    "voidTitle": "This adjustment is void",
    "voidReason": "Reason: {{reason}}",
    "void": {
      "action": "Void",
      "title": "Void {{number}}?",
      "body": "Its stock movements are reversed and its journal entry is reversed on the same date.",
      "reason": "Reason",
      "confirm": "Void adjustment"
    },
    "lineErrors": {
      "chooseWarehouse": "Choose a warehouse",
      "chooseProduct": "Choose a product",
      "duplicateProduct": "This product is already on another line",
      "quantityRequired": "Enter a quantity",
      "openingPositive": "Opening quantities must be above zero",
      "changeNonZero": "Enter the change in quantity (not zero)",
      "countNotNegative": "A counted quantity can't be negative",
      "wholeUnits": "This product is counted in whole units",
      "costRequired": "Enter the cost per unit of the stock added",
      "costFormat": "Enter a cost of 0 or more, with up to 6 decimals",
      "chooseReason": "Choose why the stock changed",
      "notOnHand": "Not that much is on hand in this warehouse; an adjustment can't take stock below zero",
      "serialCount": "Enter one serial number per unit: {{needed}} needed, {{given}} given",
      "serialCountDifference": "Enter one serial number per unit of the difference from stock: {{needed}} needed, {{given}} given"
    },
    "errors": {
      "notPosted": "The adjustment wasn't posted",
      "service": "Services aren't stocked. Choose a product that is goods.",
      "alreadyVoid": "This adjustment is already void."
    },
    "toast": {
      "posted": "Adjustment {{number}} posted",
      "voided": "Adjustment {{number}} voided"
    },
    "title": "Stock adjustments",
    "subtitle": "Opening stock, adjustments and counts, with their value at cost.",
    "new": "New adjustment",
    "views": "Adjustment views",
    "view": {
      "all": "All",
      "opening": "Opening",
      "adjustment": "Adjustments",
      "count": "Counts",
      "void": "Void"
    },
    "searchPlaceholder": "Search number or notes…",
    "searchLabel": "Search adjustments",
    "clearSearch": "Clear search",
    "reasonFilter": "Reason",
    "allReasons": "All reasons",
    "warehouseFilter": "Warehouse",
    "allWarehouses": "All warehouses",
    "emptyFiltered": "No adjustments match.",
    "clearFilters": "Clear filters",
    "empty": {
      "title": "No stock adjustments yet.",
      "body": "Record opening stock, a write-off or a count.",
      "view": {
        "opening": "No opening stock recorded.",
        "adjustment": "No adjustments.",
        "count": "No counts.",
        "void": "No void adjustments."
      }
    },
    "rowActions": "Actions for {{number}}",
    "actions": {
      "open": "Open"
    },
    "chooseReason": "Choose a reason",
    "countHint": "On hand is what the system holds in this warehouse now. Posting compares the count with what was on hand at the end of the count date.",
    "onHandFailed": "Unavailable",
    "hints": {
      "reason": "Why the stock changed: it is reported and filtered by."
    },
    "reasonCode": {
      "DAMAGE": "Damaged",
      "EXPIRED": "Expired",
      "THEFT_LOSS": "Theft or loss",
      "FOUND": "Found",
      "CORRECTION": "Correction",
      "OTHER": "Other"
    },
    "stats": {
      "label": "Adjustment figures",
      "writeOffs": "Written off",
      "gains": "Stock gained",
      "monthDetail": "{{month}} · {{documents}}",
      "trend": "Stock written off by month, {{from}} to {{to}}",
      "counts": "Stock counts",
      "countsDetail": "Posted counts",
      "void": "Void",
      "voidDetail": "Voided and reversed",
      "documents_one": "{{n}} document",
      "documents_other": "{{n}} documents"
    },
    "figures": "Adjustment figures",
    "valueIn": "Added to stock at cost",
    "valueOut": "Taken out of stock at cost",
    "valueNone": "No change in value",
    "increased": "Increased",
    "decreased": "Decreased",
    "linesCount_one": "{{n}} product",
    "linesCount_other": "{{n}} products",
    "openJournal": "Open the journal entry",
    "journalPosted": "Posted to the ledger",
    "noJournal": "Nothing to post",
    "sectionHint": "Where the stock is, on which day, and what kind of change it is."
  },
  "transfers": {
    "title": "Stock transfers",
    "subtitle": "Goods moved from one warehouse to another. The value of stock does not change.",
    "new": "New transfer",
    "newTitle": "New stock transfer",
    "newSubtitle": "Saved as a draft. Posting moves the goods; the number is assigned then.",
    "prefillFailed": "The product could not be loaded. Pick it on the line instead.",
    "editTitle": "Edit {{title}}",
    "editSubtitle": "Only a draft can be changed.",
    "draftTitle": "Draft transfer",
    "saveDraft": "Save draft",
    "notDraftTitle": "This transfer is no longer a draft",
    "backToTransfer": "Back to the transfer",
    "voidTitle": "This transfer is void",
    "draftNote": "A draft doesn't move any stock. Post it when the goods are sent.",
    "searchLabel": "Search transfers",
    "searchPlaceholder": "Search number or notes…",
    "clearSearch": "Clear search",
    "statusFilter": "Filter by status",
    "allStatuses": "All statuses",
    "warehouseFilter": "Filter by warehouse",
    "allWarehouses": "All warehouses",
    "emptyFiltered": "Nothing matches these filters.",
    "clearFilters": "Clear filters",
    "chooseWarehouse": "Choose a warehouse",
    "chooseProduct": "Choose a product",
    "addLine": "Add product",
    "removeLine": "Remove line {{n}}",
    "lineActions": "Line actions",
    "enterHint": "Enter in the last line adds another.",
    "productOfLine": "Product on line {{n}}",
    "quantityOfLine": "Quantity on line {{n}}",
    "status": {
      "DRAFT": "Draft",
      "POSTED": "Posted",
      "VOID": "Void"
    },
    "sections": {
      "transfer": "Transfer",
      "lines": "Products",
      "route": "Route"
    },
    "fields": {
      "number": "Number",
      "date": "Transfer date",
      "from": "From warehouse",
      "to": "To warehouse",
      "route": "From → to",
      "lineCount": "Products",
      "notes": "Notes",
      "product": "Product",
      "quantity": "Quantity",
      "value": "Value at cost",
      "totalValue": "Total value moved",
      "status": "Status",
      "updatedAt": "Last changed",
      "createdBy": "Created by",
      "postedAt": "Posted"
    },
    "hints": {
      "lines": "Goods only; each product once. The quantity must be on hand in the source warehouse when you post.",
      "value": "Goods leave at their average cost and arrive at exactly that value."
    },
    "lineErrors": {
      "chooseProduct": "Choose a product",
      "wholeUnits": "This product is counted in whole units",
      "service": "Services aren't stocked. Choose a product that is goods.",
      "duplicate": "This product is already on another line",
      "sameWarehouse": "Choose a different warehouse to transfer to",
      "serialCount": "Enter one serial number per unit: {{needed}} needed, {{given}} given",
      "serialsOver": "More serial numbers than units: {{given}} for {{needed}}. Remove some or raise the quantity."
    },
    "serialsMissingTitle": "Serial numbers missing",
    "serialsMissing": "Each serial-tracked line needs one serial number per unit before posting. Edit the transfer to add them.",
    "errors": {
      "STOCK_TRANSFER_POSTED": "Only a draft transfer can be edited, posted or deleted. A posted one can only be voided.",
      "STOCK_TRANSFER_NOT_POSTED": "Only a posted transfer can be voided; delete a draft instead.",
      "STOCK_NOT_ON_HAND": "Some of these goods aren't on hand in the source warehouse, so they can't be transferred.",
      "STOCK_ALREADY_USED": "Some of the goods this transfer brought in have been used since. Transfer them back instead of voiding.",
      "WAREHOUSE_ARCHIVED": "One of the warehouses is archived. Choose an active one."
    },
    "actions": {
      "post": "Post transfer",
      "delete": "Delete draft",
      "void": "Void transfer",
      "open": "Open",
      "edit": "Edit draft",
      "print": "Print"
    },
    "post": {
      "title": "Post this transfer?",
      "body": "The goods leave {{from}} and arrive at {{to}} on {{date}}, at their average cost. The value of stock does not change."
    },
    "delete": {
      "title": "Delete this draft?",
      "body": "The draft is removed. No stock has moved."
    },
    "void": {
      "title": "Void {{number}}?",
      "body": "The goods are moved back to the source warehouse, provided they are still on hand where they arrived. The transfer stays on record, marked void."
    },
    "toast": {
      "saved": "Draft transfer saved",
      "posted": "Transfer posted",
      "deleted": "Draft deleted",
      "voided": "{{number}} voided"
    },
    "empty": {
      "title": "No stock transfers yet.",
      "body": "Move goods from one warehouse to another."
    },
    "loadingProduct": "Loading the product…",
    "rowActions": "Actions for {{number}}",
    "print": {
      "title": "Stock transfer",
      "from": "From",
      "to": "To",
      "dispatchedBy": "Dispatched by",
      "receivedBy": "Received by",
      "product": "Product",
      "code": "SKU / barcode",
      "quantity": "Quantity",
      "back": "Back to transfer",
      "voidBanner": "Void on {{date}}: these goods were moved back.",
      "signatures": "Signatures",
      "nameAndDate": "Name, signature and date",
      "fixedLine": "Quantities only; no prices.",
      "lineCount_one": "{{count}} line",
      "lineCount_other": "{{count}} lines"
    },
    "stats": {
      "label": "Transfer figures",
      "drafts": "Drafts",
      "draftsDetail": "Waiting to be posted",
      "moved": "Moved",
      "movedDetail": "{{month}} · {{transfers}}",
      "trend": "Value moved by month, {{from}} to {{to}}",
      "posted": "Posted",
      "postedDetail": "Goods moved",
      "void": "Void",
      "voidDetail": "Moved back",
      "transfers_one": "{{n}} transfer",
      "transfers_other": "{{n}} transfers"
    },
    "productCount_one": "{{n}} product",
    "productCount_other": "{{n}} products",
    "saveHint": "Press {{shortcut}} to save",
    "sectionHint": "Where the goods leave, where they arrive, and on which day."
  },
  "serialErrors": {
    "SERIAL_COUNT_MISMATCH": "Enter one serial number per unit",
    "countMismatch": "Enter one serial number per unit of the difference between the count and the stock on the count date",
    "SERIAL_DUPLICATE": "Listed twice, or already in stock or sold: {{serials}}",
    "SERIAL_NOT_AVAILABLE": "Not in stock at this warehouse: {{serials}}",
    "notReceivable": "Can't be received here: {{serials}}",
    "SERIALS_NOT_TRACKED": "This product doesn't track serial numbers. Remove them.",
    "other": "Check the serial numbers on this line"
  },
  "serials": {
    "mark": "S/N",
    "tally": "{{count}} / {{quantity}}",
    "tallyLabel": "Serial numbers for {{product}}: {{count}} of {{quantity}}",
    "tallyLabelOpen": "Serial numbers for {{product}}: {{count}}",
    "title": "Serial numbers · {{product}}",
    "hint": {
      "new": "Scan each unit's serial number, or type it and press Enter. Paste a list to add several at once.",
      "inStock": "Scan the serial number on each unit going out, or tick it below. Only serial numbers in stock at this warehouse are listed.",
      "sold": "Scan or tick the serial numbers coming back. Only serial numbers sold on this invoice line are listed."
    },
    "scan": "Serial number",
    "scanPlaceholder": "Scan or type, then press Enter",
    "enteredHeading": "Entered",
    "chosenHeading": "Ticked",
    "chooseHeading": "Serial numbers to choose from",
    "goneHeading": "No longer available",
    "progress": "{{count}} of {{quantity}}",
    "entered_one": "{{count}} serial number",
    "entered_other": "{{count}} serial numbers",
    "added": "Added {{serial}}",
    "duplicate": "{{serial}} is already on the list",
    "ticked": "Ticked {{serial}}",
    "alreadyTicked": "{{serial}} is already ticked",
    "notInStock": "{{serial}} isn't in stock at this warehouse",
    "notSold": "{{serial}} wasn't sold on this line, or has already come back",
    "remove": "Remove {{serial}}",
    "empty": {
      "new": "No serial numbers yet. Scan the first unit.",
      "inStock": "No serial numbers of this product are in stock at this warehouse.",
      "sold": "Nothing left to return on this line."
    },
    "gone": {
      "inStock": "Not in stock here any more",
      "sold": "Not on this line any more"
    },
    "noMatch": "No serial number matches “{{q}}”.",
    "quantityBecomes": "The line quantity becomes {{count}}.",
    "cancel": "Cancel",
    "use_one": "Use {{count}} serial number",
    "use_other": "Use {{count}} serial numbers",
    "clear": "Clear serial numbers",
    "chooseWarehouse": "Choose the warehouse first: serial numbers are picked from its stock.",
    "more_one": "+{{count}} more",
    "more_other": "+{{count}} more",
    "pageTitle": "Serial numbers",
    "subtitle": "Find a unit by its serial number: where it is, and every document that moved it.",
    "tabTitle": "Serial numbers",
    "searchLabel": "Search serial numbers",
    "searchPlaceholder": "Scan or type a serial number…",
    "clearSearch": "Clear search",
    "statusFilter": "Status",
    "allStatuses": "All statuses",
    "warehouseFilter": "Warehouse",
    "allWarehouses": "All warehouses",
    "emptyTitle": "No serial numbers yet",
    "emptyBody": "They appear when goods of a serial-tracked product are received.",
    "emptyFiltered": "No serial number matches these filters.",
    "inWarehouse": "in {{warehouse}}",
    "historyTitle": "History",
    "status": {
      "IN_STOCK": "In stock",
      "SOLD": "Sold",
      "RETURNED_TO_SUPPLIER": "Returned to supplier",
      "REMOVED": "Removed",
      "ISSUED_TO_PROJECT": "Issued to a project"
    },
    "reason": {
      "OPENING": "Opening stock",
      "PURCHASE": "Received on a purchase",
      "PURCHASE_RETURN": "Returned to the supplier",
      "SALE": "Sold",
      "SALE_RETURN": "Returned by the customer",
      "TRANSFER": "Transferred",
      "ADJUSTMENT": "Stock adjustment",
      "PROJECT_ISSUE": "Issued to a project",
      "PROJECT_RETURN": "Returned from a project"
    },
    "fields": {
      "serial": "Serial number",
      "product": "Product",
      "status": "Status",
      "warehouse": "Warehouse",
      "receivedBy": "Received by",
      "issuedBy": "Issued by",
      "updatedAt": "Last moved"
    },
    "history": {
      "date": "Date",
      "what": "What happened",
      "status": "Status after",
      "warehouse": "Warehouse",
      "document": "Document",
      "by": "By",
      "system": "System",
      "reversal": "Void",
      "empty": "No movements recorded."
    },
    "useNone": "Use serial numbers",
    "legacyImport": "From CloudTech, before cut-over",
    "whereTitle": "Where it is"
  }
}
